import { Fragment, useId, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { Field } from '@base-ui/react/field'

import { isUuid } from '@/shared/lib/entity'
import { cn } from '@/lib/utils'
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogTitle } from '@/ui/alert-dialog'
import { DialogFooter } from '@/ui/dialog'
import { Button } from '@/ui/button'
import { Frame, FrameCard, FrameConnector } from '@/ui/frame'
import { Input, inputClassName } from '@/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/select'
import { Switch } from '@/ui/switch'
import { readSelectorGui, selectorExpression, type SelectorGui, type SelectorCondition, type SelectorField, ENTITY_SELECTOR_FIELDS, SELECTOR_COMPARISONS } from './policy-selector-model'

type Condition = SelectorCondition & { id: string }
const identityComparisons = SELECTOR_COMPARISONS.filter((comparison) =>
  comparison.value === 'equals' || comparison.value === 'not-equals',
)
const maximumSelectorLength = 4096

function newCondition(fields: readonly SelectorField[]): Condition {
  const field = fields[0]?.value ?? 'target.id'
  return { id: crypto.randomUUID(), field, comparison: field === 'target.id' ? 'equals' : 'contains', value: '' }
}

export function PolicySelector({ value, gui, disabled, replacementVersion, onChange, targetFields = ENTITY_SELECTOR_FIELDS, targetLabel = 'entities' }: {
  entityId: string; value: string; gui?: unknown; disabled: boolean
  replacementVersion?: number
  targetFields?: readonly SelectorField[]; targetLabel?: string
  onChange: (expression: string, valid: boolean, gui: SelectorGui | null) => void
}) {
  const fields = targetFields
  const switchId = useId()
  const [initialGui] = useState(() => readSelectorGui(gui, value, fields))
  const [advanced, setAdvanced] = useState(() => Boolean(value) && initialGui?.mode !== 'matching')
  const [hasStructuredSelector, setHasStructuredSelector] = useState(() => !value || initialGui?.mode === 'matching')
  const [confirmMatching, setConfirmMatching] = useState(false)
  const [preservedDrafts, setPreservedDrafts] = useState<string[]>([])
  const [groups, setGroups] = useState<Condition[][]>(() =>
    initialGui?.mode === 'matching'
      ? initialGui.groups.map((group) => group.map((condition) => ({ ...condition, id: crypto.randomUUID() })))
      : [],
  )
  const [seenReplacement, setSeenReplacement] = useState(replacementVersion)
  const [previousValue, setPreviousValue] = useState(value)
  if (previousValue !== value) setPreviousValue(value)
  if (seenReplacement !== replacementVersion) {
    const replacement = readSelectorGui(gui, value, fields)
    setSeenReplacement(replacementVersion)
    if (advanced && previousValue !== value && !preservedDrafts.includes(previousValue)) {
      setPreservedDrafts([...preservedDrafts, previousValue])
    }
    setAdvanced(Boolean(value) && replacement?.mode !== 'matching')
    setHasStructuredSelector(!value || replacement?.mode === 'matching')
    setGroups(replacement?.mode === 'matching'
      ? replacement.groups.map((group) => group.map((condition) => ({ ...condition, id: crypto.randomUUID() })))
      : [])
    setConfirmMatching(false)
  }

  function changeGroups(next: Condition[][]) {
    setGroups(next)
    const expression = selectorExpression(next, fields)
    const complete = next.every((group) =>
      group.every((condition) => condition.field === 'target.id'
        ? isUuid(condition.value.trim())
        : condition.value.trim().length > 0),
    )
    onChange(expression, complete && expression.length <= maximumSelectorLength, next.length ? {
      version: 1,
      mode: 'matching',
      groups: next.map((group) => group.map(({ field, comparison, value }) => ({ field, comparison, value }))),
    } : null)
  }

  function changeAdvanced(next: boolean) {
    if (!next && (!hasStructuredSelector || selectorExpression(groups, fields) !== value)) {
      setConfirmMatching(true)
      return
    }
    setAdvanced(next)
    if (next) {
      const expression = selectorExpression(groups, fields)
      onChange(expression, expression.length <= maximumSelectorLength, { version: 1, mode: 'expression' })
    } else changeGroups(groups)
  }

  function restoreConditions() {
    setPreservedDrafts((drafts) => drafts.includes(value) ? drafts : [...drafts, value])
    setConfirmMatching(false)
    setAdvanced(false)
    setHasStructuredSelector(true)
    changeGroups(groups)
  }

  function updateCondition(id: string, changes: Partial<Condition>) {
    const next = groups.map((group) => group.map((condition) => {
      if (condition.id !== id) return condition
      const updated = { ...condition, ...changes }
      if (updated.field === 'target.id' && updated.comparison !== 'equals' && updated.comparison !== 'not-equals') {
        updated.comparison = 'equals'
      }
      return updated
    }))
    changeGroups(next)
  }

  function removeCondition(id: string) {
    const next = groups
      .map((group) => group.filter((condition) => condition.id !== id))
      .filter((group) => group.length > 0)
    changeGroups(next)
  }

  function addCondition(groupIndex: number) {
    const next = groups.map((group, index) =>
      index === groupIndex ? [...group, newCondition(fields)] : group,
    )
    changeGroups(next)
  }

  return (
    <>
      <Frame
        title="Policy selector"
        description={`Choose which ${targetLabel} within the scope this policy applies to.`}
        actions={
          <div className="flex shrink-0 items-center gap-2">
            <label htmlFor={switchId} className="text-xs font-medium">Advanced Mode</label>
            <Switch id={switchId} checked={advanced} disabled={disabled} onCheckedChange={changeAdvanced} />
          </div>
        }
      >
        <FrameCard className="flex flex-col gap-4 p-4">
          {!advanced && (
            <div className="flex flex-col gap-1.5">
              {groups.map((group, groupIndex) => (
                <Fragment key={group[0]?.id}>
                  {groupIndex > 0 && <FrameConnector>OR</FrameConnector>}
                  <FrameCard className="flex flex-col items-start gap-2 p-4" role="group" aria-label={`Group ${groupIndex + 1}`}>
                    {group.map((condition, conditionIndex) => {
                      const label = `Group ${groupIndex + 1} condition ${conditionIndex + 1}`
                      return (
                        <div key={condition.id} className="border-border flex max-w-full flex-wrap items-center rounded-lg border shadow-xs">
                          <span className="bg-muted/50 text-muted-foreground px-2.5 py-2 text-xs">{conditionIndex === 0 ? 'When' : 'And'}</span>
                          <SelectorChoice label={`${label} property`} value={condition.field} options={fields} disabled={disabled} onChange={(field) => updateCondition(condition.id, { field })} />
                          <SelectorChoice label={`${label} comparison`} value={condition.comparison} options={condition.field === 'target.id' ? identityComparisons : SELECTOR_COMPARISONS} disabled={disabled} onChange={(comparison) => updateCondition(condition.id, { comparison })} />
                          <Input aria-label={`${label} value`} className="w-44 rounded-none border-0 border-x shadow-none" placeholder={condition.field === 'target.id' ? 'Entity UUID' : 'e.g. Acme'} value={condition.value} required disabled={disabled} onChange={(event) => updateCondition(condition.id, { value: event.target.value })} />
                          <Button type="button" variant="ghost" size="icon-sm" aria-label={`Remove ${label.toLowerCase()}`} disabled={disabled} onClick={() => removeCondition(condition.id)}><Trash2 aria-hidden /></Button>
                        </div>
                      )
                    })}
                    <Button type="button" variant="ghost" size="sm" disabled={disabled} onClick={() => addCondition(groupIndex)}><Plus aria-hidden />And</Button>
                  </FrameCard>
                </Fragment>
              ))}
              {groups.length === 0 && <p className="text-muted-foreground text-xs">All {targetLabel} in scope match. Add a condition to narrow the selection.</p>}
              <Button type="button" variant="outline" size="icon-sm" aria-label={groups.length ? 'Add alternative group (OR)' : 'Add condition'} disabled={disabled} onClick={() => changeGroups([...groups, [newCondition(fields)]])}><Plus aria-hidden /></Button>
              {value.length > maximumSelectorLength && <p role="alert" className="text-destructive text-xs">The selector is too long. Remove some conditions.</p>}
            </div>
          )}
          {advanced && (
            <Field.Root name="selectorExpression" className="flex flex-col gap-1.5">
              <Field.Label className="text-xs font-medium">CEL expression</Field.Label>
              <Field.Control
                value={value}
                disabled={disabled}
                aria-invalid={value.length > maximumSelectorLength || undefined}
                onChange={(event) => {
                  const expression = event.target.value
                  onChange(expression, expression.length <= maximumSelectorLength, { version: 1, mode: 'expression' })
                }}
                render={<textarea className={cn(inputClassName, 'h-24 font-mono')} />}
              />
              <Field.Description className="text-muted-foreground text-xs">
                An expression over {fields.map((field) => field.value).join(', ')}. Leave blank to match all {targetLabel}.
              </Field.Description>
              <Field.Error match={value.length > maximumSelectorLength} role="alert" className="text-destructive text-xs">
                The selector is too long. Shorten the expression.
              </Field.Error>
            </Field.Root>
          )}
          {preservedDrafts.length > 0 && (
            <details className="border-border rounded-lg border p-3 text-xs">
              <summary className="cursor-pointer font-medium">Preserved advanced draft</summary>
              <p className="text-muted-foreground my-2">Select and copy your advanced drafts. They are retained only while this editor stays open.</p>
              {preservedDrafts.map((draft, index) => (
                <textarea key={index} aria-label={`Preserved CEL draft ${index + 1}`} className={cn(inputClassName, 'mt-2 h-24 font-mono')} value={draft} readOnly />
              ))}
            </details>
          )}
        </FrameCard>
      </Frame>
      <AlertDialog open={confirmMatching && !disabled} onOpenChange={setConfirmMatching}>
        <AlertDialogContent>
          <AlertDialogTitle>{hasStructuredSelector ? 'Restore previous conditions?' : 'Start a new structured selector?'}</AlertDialogTitle>
          <AlertDialogDescription>
            {hasStructuredSelector
              ? 'The CEL expression differs from your structured conditions.'
              : 'This expression has no verified structured conditions. A new structured selector starts with all targets in scope.'}
            {' '}Your advanced draft will be preserved so you can copy it while this editor stays open.
          </AlertDialogDescription>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmMatching(false)}>Keep editing CEL</Button>
            <Button type="button" disabled={disabled} onClick={restoreConditions}>{hasStructuredSelector ? 'Restore previous conditions' : 'Start new structured selector'}</Button>
          </DialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function SelectorChoice<Value extends string>({ label, value, options, disabled, onChange }: {
  label: string; value: Value; options: readonly { value: Value; label: string }[]; disabled: boolean
  onChange: (value: Value) => void
}) {
  return (
    <Select value={value} disabled={disabled} onValueChange={(next) => { if (next !== null) onChange(next) }}>
      <SelectTrigger aria-label={label} className="w-auto rounded-none border-0 bg-transparent shadow-none">
        <SelectValue>{options.find((option) => option.value === value)?.label}</SelectValue>
      </SelectTrigger>
      <SelectContent>{options.map((option) => <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>)}</SelectContent>
    </Select>
  )
}
